import { BackEndValueType, ESymbolCategory, ISymbol, IWFParameter, IWFStep, IWorkFlow } from "@kuika/kuika-cl-model";
import _ from "lodash";

/**
 * Process-parameter validation and mutation, lifted out of the left panel's fields
 * editor so the Parameters tab owns the same semantics.
 *
 * The propagation in `applyWorkflowParameterUpdate` / `…Removal` is the part worth
 * protecting: a parameter is referenced by every step's `userInteractions[].parameters`
 * and `outputParameters`, so renaming or retyping one has to reset those bound values
 * (they would otherwise hold a symbol of the wrong type) and deleting one has to strip
 * them. Losing that is silent — the workflow still saves, then miscompiles.
 */

/** Steps nest through subprocesses; the provider owns the traversal. */
export type WorkflowStepIterator = (steps: IWFStep[], visit: (step: IWFStep) => void) => void;

/** Names the workflow engine reserves on every business object. */
export const WORKFLOW_RESERVED_PARAM_NAMES: string[] = [
  "id",
  "typename",
  "name",
  "eventname",
  "eventkey",
  "effectivedate",
  "assignmenttype",
  "assignmentkey",
  "description",
  "duedate"
];

/**
 * The "Primitive Type" tab vocabulary lives in the reusable data-type dropdown so there is
 * one source; the workflow layer re-exports it under the shared name.
 */
export { DATA_TYPE_PRIMITIVES } from "../../shared/kuika-data-type-dropdown/kuika-data-type-dropdown";

/**
 * Parameter names become C# identifiers, so they cannot start with a digit or carry
 * anything but ASCII letters and digits.
 */
export const WORKFLOW_PARAMETER_NAME_PATTERN: RegExp = /^[A-Za-z][A-Za-z0-9]+$/;

/** Seed value for a freshly typed parameter, keyed off its backend type. */
export const getDefaultFixedValue = (dataType: string | undefined): string | number | boolean => {
  switch (dataType) {
    case "Boolean":
      return false;
    case "DateTimeOffset":
      return new Date().toISOString();
    case "Int16":
    case "Int32":
    case "Int64":
    case "Single":
    case "Decimal":
    case "Double":
      return 0;
    case "Unknown":
    case "Guid":
    case "String":
    default:
      return "";
  }
};

export interface IValidateWorkflowParameterInput {
  parameter: IWFParameter;
  existingParameters: readonly IWFParameter[];
  /** `project.commonReservedKeys`; merged with the workflow-specific names above. */
  reservedKeys?: readonly string[];
  /** Set when editing, so a parameter does not collide with itself. */
  originalParameterId?: string;
  /** Pre-edit snapshot. When deep-equal to `parameter`, the edit is a no-op. */
  originalParameter?: IWFParameter;
}

/**
 * Returns the user-facing warning, or null when the parameter is valid. Messages are
 * kept verbatim from the panel this replaces.
 */
export const validateWorkflowParameter = (input: IValidateWorkflowParameterInput): string | null => {
  const { parameter, existingParameters, reservedKeys, originalParameterId, originalParameter } = input;
  const name: string = parameter.name ?? "";

  if (name === "" || parameter.dataType === undefined) {
    return "Please fill all fields!";
  }
  if (!WORKFLOW_PARAMETER_NAME_PATTERN.test(name)) {
    return "Field accepts only english letters.";
  }

  const reserved: string[] = [...(reservedKeys ?? []), ...WORKFLOW_RESERVED_PARAM_NAMES].map((key: string): string =>
    key.toLowerCase()
  );
  if (reserved.includes(name.toLowerCase())) {
    return "C# reserved keywords and system names cannot be used as parameter names.";
  }

  if (originalParameter && _.isEqual(originalParameter, parameter)) {
    return "There is no change to update!";
  }

  const duplicate: IWFParameter | undefined = existingParameters.find(
    (p: IWFParameter): boolean => p.name === name && p.id !== originalParameterId
  );
  if (duplicate) {
    return originalParameterId ? `There is already a parameter with name "${name}"!` : "Field name already exists!";
  }

  return null;
};

/** The Fixed Value symbol a bound reference falls back to once its parameter changes. */
export const buildParameterResetSymbol = (parameter: IWFParameter): ISymbol =>
  ({
    ...parameter.defaultValue,
    label: "Fixed Value",
    category: ESymbolCategory.fixed,
    fixedValue: getDefaultFixedValue(parameter.dataType)
  }) as ISymbol;

/**
 * Applies an edit in place and re-seeds every step reference to it. Assumes the caller
 * has already validated; returns false when the id is not in the workflow.
 */
export const applyWorkflowParameterUpdate = (
  workflow: IWorkFlow,
  originalParameterId: string,
  next: IWFParameter,
  iterateSteps: WorkflowStepIterator
): boolean => {
  const target: IWFParameter | undefined = workflow.parameters?.find(
    (p: IWFParameter): boolean => p.id === originalParameterId
  );
  if (!target) return false;

  Object.assign(target, next);
  const resetValue: ISymbol = buildParameterResetSymbol(next);

  // An object (Kuika Data Type) parameter cannot be an interaction parameter and cannot be
  // produced by a fixed scalar value, so when a parameter becomes an object it is stripped
  // from every interaction and its output binding is cleared (the user re-binds it field by
  // field / at the screen side). A scalar keeps its bindings but has the value reset to a
  // fresh default of the new type. Ported from the pre-tab fields editor (KOA-15840,
  // "wf object parameter datatype change fix", vtozkan).
  iterateSteps(workflow.nodes?.steps ?? [], (step: IWFStep): void => {
    step?.userInteractions?.forEach((interaction) => {
      if (next.isObject) {
        interaction.parameters = interaction.parameters?.filter((p) => p.id !== originalParameterId);
        return;
      }
      interaction.parameters?.forEach((p) => {
        if (p.id === originalParameterId) p.value = resetValue;
      });
    });
    const output = step?.outputParameters?.find((p) => p.id === originalParameterId);
    if (output) {
      if (next.isObject) {
        output.value = undefined;
        output.parameters = undefined;
      } else {
        output.value = resetValue;
      }
    }
  });

  return true;
};

/** Removes a parameter and strips every step reference to it. */
export const applyWorkflowParameterRemoval = (
  workflow: IWorkFlow,
  parameterId: string,
  iterateSteps: WorkflowStepIterator
): boolean => {
  const index: number = workflow.parameters?.findIndex((p: IWFParameter): boolean => p.id === parameterId) ?? -1;
  if (index < 0) return false;

  workflow.parameters?.splice(index, 1);

  iterateSteps(workflow.nodes?.steps ?? [], (step: IWFStep): void => {
    const outputIndex: number = step?.outputParameters?.findIndex((p) => p.id === parameterId) ?? -1;
    if (outputIndex > -1) step.outputParameters?.splice(outputIndex, 1);

    step?.userInteractions?.forEach((interaction) => {
      const uiIndex: number = interaction.parameters?.findIndex((p) => p.id === parameterId) ?? -1;
      if (uiIndex > -1) interaction.parameters?.splice(uiIndex, 1);
    });
  });

  return true;
};

/** A blank draft row for the Parameters table's create flow. */
export const buildDraftParameter = (id: string, isBusinessObject: boolean): IWFParameter =>
  ({
    id,
    name: "",
    dataType: "String" as BackEndValueType,
    isBusinessObject,
    isInput: false,
    isOutput: false,
    defaultValue: {
      category: ESymbolCategory.fixed,
      label: "Fixed Value",
      description: ""
    }
  }) as IWFParameter;
